// Copyright (c) 2023, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 Inc. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import * as React from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import BackdropProgress from '../../BackdropProgress';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ConfirmationDialog from '../dialog/ConfirmationDialog';
import ClearIcon from "@material-ui/icons/Clear";
import CustomSnackbar from '../ui/Snackbars';
import Dialog from '@mui/material/Dialog';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import useHttp from '../../utils/http';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { adjustForTimezone, getCountryNameFromCountryCode, getDateForDB, getCountryList } from '../../utils/formatting';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { entryTypes, travelDocumentTypes, visaTypes } from '../../utils/const';
import { getEmployeeEmails } from '../../utils/oauth';
import { postSearchTravelDocuments, postVisaImage, postEmployeeVisa } from '../../utils/end-points.js';
import { FileUploader } from "react-drag-drop-files";
import { IconButton } from "@material-ui/core";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { makeStyles } from '@mui/styles';
import { useState, useEffect } from 'react';
import "../../css/AddVisaDialog.scss"

const useStyles = makeStyles(() => ({
    paper: { minWidth: "700px" },
}));

function AddVisaDialog(props) {

    const classes = useStyles();
    const { open, onClose, visaList, refreshVisaList } = props
    const { handleRequest } = useHttp()
    const employees = getEmployeeEmails()
    const [passportList, setPassportList] = useState([])
    const [isPassoportsLoading, setPassportsLoading] = useState(false)
    const [email, setEmail] = useState(null)
    const [country, setCountry] = useState(null)
    const [visaType, setVisaType] = useState(null)
    const [entryType, setEntryType] = useState(null)
    const [validFrom, setValidFrom] = useState(null)
    const [validTo, setValidTo] = useState(null)
    const [issuedDate, setIssuedDate] = useState(null)
    const [passport, setPassport] = useState(null)
    const [visaImage, setVisaImage] = useState(null)
    const [isFormError, setFormError] = useState(false)
    const [isDatesError, setDateError] = useState(false)
    const [isLoading, setLoading] = useState(false)
    const [snackbarData, setSnackbarData] = useState({ open: false, message: "", severity: "", onClose: null })
    const [confirmationDialogData, setConfirmationDialogData] = useState({ open: false, title: "", content: "", acceptLabel: "", declineLabel: "", acceptAlert: null, declineAlert: null })

    useEffect(() => {
        fetchData()
    }, [email])

    const fetchData = () => {

        setPassportsLoading(true)

        let body = {
            status: "active",
            offset: 0,
            email: email,
            documentType: travelDocumentTypes.PASSPORT,
        }
        handleRequest(postSearchTravelDocuments, "POST", body, (passports) => {
            if (passports) {
                var currentDate = new Date()
                let passportOptions = []
                passports = passports.filter(document => document.documentType === travelDocumentTypes.PASSPORT)
                passports.map(passport => {
                    var passportEndDate = adjustForTimezone(new Date(passport.expiryDate))
                    if (currentDate <= passportEndDate) {
                        passport['isExpired'] = false
                        passportOptions.push(passport)
                    }
                })
                passports.map(passport => {
                    var passportEndDate = adjustForTimezone(new Date(passport.expiryDate))
                    if (currentDate > passportEndDate) {
                        passport['isExpired'] = true
                        passportOptions.push(passport)
                    }
                })
                setPassportList(passportOptions)
            }
            else if (!passports) {
                setSnackbarData({ message: "Error while fetching Travel Documents", open: true, severity: "error", onClose: onCloseSnackbar })
            }
            setPassportsLoading(false)
        }, (error) => {
            setPassportsLoading(false)
            console.error(error)
            setSnackbarData({ message: error, open: true, severity: "error", onClose: onCloseSnackbar })
        })
    }

    const handleSubmit = () => {

        setDateError(false)
        setFormError(false)
        if (!email || !country || !visaType || !entryType || !validFrom || !validTo || !issuedDate || !visaImage || !passport) {
            setFormError(true)
            setSnackbarData({ message: "Please check the form again", open: true, severity: "error", onClose: onCloseSnackbar })
        }
        else {
            setFormError(false)
            if (validFrom > validTo) {
                setSnackbarData({ message: "Please check the dates again", open: true, severity: "error", onClose: onCloseSnackbar })
                setDateError(true)
            }
            else {
                console.log(visaList)
                let existingVisa = visaList.filter(visa => visa.country === country.countryCode && visa.email === email)
                console.log(existingVisa)
                let contentMsg = existingVisa.length > 0
                    ? "There is an already existing Visa for the same User and the same Country. This will overide that existing Visa and it can not be undone. Please make sure the information are correct. Are you sure you want to override and add this Visa?"
                    : "Please make sure the information are correct. Are you sure you want to add this Visa?"

                setConfirmationDialogData({
                    open: true,
                    title: "Add New Visa",
                    content: contentMsg,
                    acceptLabel: "Yes",
                    declineLabel: "No",
                    acceptAlert: saveVisa,
                    declineAlert: handleDeclineAlert
                })
            }
        }

    }

    const saveVisa = () => {

        setConfirmationDialogData(data => ({ ...data, open: false }))
        onClose()
        setLoading(true)
        setDateError(false)

        const body = {
            email: email,
            country: country.countryCode,
            visaType: visaType,
            startDate: getDateForDB(validFrom),
            endDate: getDateForDB(validTo),
            entryType: entryType,
            issuedDate: getDateForDB(issuedDate),
            passportId: passport.id
        }
        handleRequest(postEmployeeVisa, "POST", body, (response) => {
            if (response && response.id && response.id > 0) {

                let fileExtension = visaImage.name.substring(visaImage.name.lastIndexOf('.') + 1, visaImage.name.length)
                let fileName = country.countryCode + "." + fileExtension;

                const reader = new FileReader()
                reader.readAsDataURL(visaImage);
                reader.onload = () => {
                    const encodedFile = btoa(reader.result)
                    const fileBody = {
                        encodedFile: encodedFile
                    }
                    handleRequest(postVisaImage.replace("$visaId", response.id).replace("$imageName", fileName), "POST", fileBody, (visa) => {
                        if (visa && visa.id && visa.id > 0) {
                            handleClose()
                            refreshVisaList()
                        }
                        else {
                            setSnackbarData({ message: "Visa image upload failed!", open: true, severity: "error", onClose: onCloseSnackbar })
                        }
                        setLoading(false)
                    }, (error) => {
                        setLoading(false)
                        console.error(error)
                        setSnackbarData({ message: error, open: true, severity: "error", onClose: onCloseSnackbar })
                    })
                };
            }
            else {
                setLoading(false)
                setSnackbarData({ message: "Visa image upload failed!", open: true, severity: "error", onClose: onCloseSnackbar })
            }
        }, (error) => {
            setLoading(false)
            console.error(error)
            setSnackbarData({ message: error, open: true, severity: "error", onClose: onCloseSnackbar })
        })
    }

    const fileUploadSizeError = () => {
        console.error("Size cannot exceed 1MB")
        setSnackbarData({ message: "Size cannot exceed 1MB", open: true, severity: "error", onClose: onCloseSnackbar })
        setVisaImage(null)
    }

    const handleClose = () => {
        onClose()
        setEmail(null)
        setCountry(null)
        setVisaType(null)
        setEntryType(null)
        setValidFrom(null)
        setValidTo(null)
        setIssuedDate(null)
        setVisaImage(null)
        setPassport(null)
        setFormError(false)
        onCloseSnackbar()
    }

    const handleDeclineAlert = () => {
        setConfirmationDialogData(data => ({ ...data, open: false }))
    }

    const onCloseSnackbar = () => {
        setSnackbarData({ ...snackbarData, open: false })
    }

    return (

        <div>

            <ConfirmationDialog confirmationDialogData={confirmationDialogData} />

            <CustomSnackbar snackbar={snackbarData} />

            <BackdropProgress open={isLoading} />

            <Dialog onClose={() => handleClose()} open={open} classes={{ paper: classes.paper }} className="Add-visa-dialog">

                <div className='Dialog-div'>

                    <div className='Dialog-title'>Add Visa</div>

                    <div>
                        <div className='Fields-required'><span className='Required-span'>*</span> All fields are required</div>

                        <Grid container className='Grid-container'>
                            <Grid item md={3} className='Grid-item'><span className='Grid-item-label'>Email</span><span className='Grid-item-required'>*</span></Grid>
                            <Grid item md={9} className='Grid-item'>
                                <Autocomplete
                                    size="small"
                                    variant="standard"
                                    fullWidth
                                    disablePortal
                                    isOptionEqualToValue={(option, value) => option.workEmail === value.workEmail}
                                    options={employees.filter(employee => employee.workEmail !== null)}
                                    getOptionLabel={(option) => option.workEmail}
                                    onChange={(event, value) => setEmail(value ? value.workEmail : null)}
                                    renderOption={(props, option) => (
                                        <Box {...props} key={option.id}>{option.workEmail}</Box>
                                    )}
                                    renderInput={(params) => <TextField  {...params} label="Traveller's Email" error={isFormError && !email} />}
                                />
                            </Grid>
                        </Grid>

                        <Grid container className='Grid-container'>
                            <Grid item md={3} className='Grid-item'><span className='Grid-item-label'>Country</span><span className='Grid-item-required'>*</span></Grid>
                            <Grid item md={9} className='Grid-item'>
                                <Autocomplete
                                    size="small"
                                    disablePortal
                                    isOptionEqualToValue={(option, value) => option.country === value.country}
                                    fullWidth
                                    onChange={(event, value) => setCountry(value)}
                                    options={getCountryList()}
                                    getOptionLabel={(option) => option.country}
                                    renderOption={(props, option) => (
                                        <Box {...props}>{option.country}</Box>
                                    )}
                                    renderInput={(params) => <TextField  {...params} label="Country" error={isFormError && !country} />}
                                />
                            </Grid>
                        </Grid>

                        <Grid container className='Grid-container'>
                            <Grid item md={3} className='Grid-item'><span className='Grid-item-label'>Visa Type</span><span className='Grid-item-required'>*</span></Grid>
                            <Grid item md={9} className='Grid-item'>
                                <Autocomplete
                                    size="small"
                                    disablePortal
                                    fullWidth
                                    onChange={(event, value) => setVisaType(value)}
                                    options={visaTypes}
                                    getOptionLabel={(option) => option}
                                    renderOption={(props, option) => (
                                        <Box {...props}>{option}</Box>
                                    )}
                                    renderInput={(params) => <TextField  {...params} label="Visa Type" error={isFormError && !visaType} />}
                                />
                            </Grid>
                        </Grid>

                        <Grid container className='Grid-container'>
                            <Grid item md={3} className='Grid-item'><span className='Grid-item-label'>Entry Type</span><span className='Grid-item-required'>*</span></Grid>
                            <Grid item md={9} className='Grid-item'>
                                <Autocomplete
                                    size="small"
                                    disablePortal
                                    fullWidth
                                    onChange={(event, value) => setEntryType(value)}
                                    options={entryTypes}
                                    getOptionLabel={(option) => option}
                                    renderOption={(props, option) => (
                                        <Box {...props}>{option}</Box>
                                    )}
                                    renderInput={(params) => <TextField  {...params} label="Entry Type" error={isFormError && !entryType} />}
                                />
                            </Grid>
                        </Grid>

                        <Grid container className='Grid-container'>
                            <Grid item md={3} className='Grid-item'><span className='Grid-item-label'>Valid From</span><span className='Grid-item-required'>*</span></Grid>
                            <Grid item md={9} className='Grid-item'>
                                <LocalizationProvider dateAdapter={AdapterDateFns} className='Date-picker'>
                                    <DatePicker
                                        className='Date-picker'
                                        label="Valid From"
                                        inputFormat='yyyy-MM-dd'
                                        mask='____-__-__'
                                        value={validFrom}

                                        onChange={(value) => setValidFrom(value)}
                                        InputProps={{
                                            endAdornment: (
                                                <IconButton onClick={() => setValidFrom(null)} size="small">
                                                    <ClearIcon />
                                                </IconButton>
                                            )
                                        }}
                                        InputAdornmentProps={{
                                            position: "start"
                                        }}
                                        renderInput={(params) => <TextField size="small" {...params} fullWidth error={(isFormError && !validFrom) || isDatesError} />}
                                    />
                                </LocalizationProvider>
                            </Grid>
                        </Grid>

                        <Grid container className='Grid-container'>
                            <Grid item md={3} className='Grid-item'><span className='Grid-item-label'>Valid To</span><span className='Grid-item-required'>*</span></Grid>
                            <Grid item md={9} className='Grid-item'>
                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                    <DatePicker
                                        label="Valid To"
                                        inputFormat='yyyy-MM-dd'
                                        mask='____-__-__'
                                        value={validTo}
                                        onChange={(value) => setValidTo(value)}
                                        InputProps={{
                                            endAdornment: (
                                                <IconButton onClick={() => setValidTo(null)} size="small">
                                                    <ClearIcon />
                                                </IconButton>
                                            )
                                        }}
                                        InputAdornmentProps={{
                                            position: "start"
                                        }}
                                        renderInput={(params) => <TextField size="small" {...params} fullWidth error={(isFormError && !validTo) || isDatesError} />}
                                    />
                                </LocalizationProvider>
                            </Grid>
                        </Grid>

                        <Grid container className='Grid-container'>
                            <Grid item md={3} className='Grid-item'><span className='Grid-item-label'>Issued Date</span><span className='Grid-item-required'>*</span></Grid>
                            <Grid item md={9} className='Grid-item'>
                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                    <DatePicker
                                        label="Issued Date"
                                        inputFormat='yyyy-MM-dd'
                                        mask='____-__-__'
                                        value={issuedDate}
                                        onChange={(value) => setIssuedDate(value)}
                                        InputProps={{
                                            endAdornment: (
                                                <IconButton onClick={() => setIssuedDate(null)} size="small">
                                                    <ClearIcon />
                                                </IconButton>
                                            )
                                        }}
                                        InputAdornmentProps={{
                                            position: "start"
                                        }}
                                        renderInput={(params) => <TextField size="small" {...params} fullWidth error={isFormError && !issuedDate} />}
                                    />
                                </LocalizationProvider>
                            </Grid>
                        </Grid>

                        <Grid container className='Grid-container'>
                            <Grid item md={3} className='Grid-item'><span className='Grid-item-label'>Passport</span><span className='Grid-item-required'>*</span></Grid>
                            <Grid item md={9} className='Grid-item'>
                                <Autocomplete
                                    size="small"
                                    noOptionsText='No active passports'
                                    loading={isPassoportsLoading}
                                    disablePortal
                                    disabled={!email}
                                    fullWidth
                                    onChange={(event, value) => setPassport(value)}
                                    options={passportList.filter(passport => passport.email === email)}
                                    getOptionDisabled={(option) => option.isExpired === true}
                                    getOptionLabel={(option) => getCountryNameFromCountryCode(option.issuedCountry) + " - " + option.documentNo + " ( " + getDateForDB(adjustForTimezone(new Date(option.issuedDate))) + " - " + getDateForDB(adjustForTimezone(new Date(option.expiryDate))) + " )"}
                                    renderOption={(props, option) => (
                                        <Box style={{ fontSize: 15, color: option.isExpired ? "red" : null }}  {...props}>{getCountryNameFromCountryCode(option.issuedCountry) + " - " + option.documentNo + " ( " + getDateForDB(adjustForTimezone(new Date(option.issuedDate))) + " - " + getDateForDB(adjustForTimezone(new Date(option.expiryDate))) + " )"}</Box>
                                    )}
                                    renderInput={(params) => <TextField {...params} label={email ? "Passport" : "Please pick Traveller's Email"} error={isFormError && !passport} />}
                                />
                            </Grid>
                        </Grid>

                        <Grid container className='Grid-container'>
                            <Grid item md={3} className='Grid-item'><span className='Grid-item-label'>Visa Image</span><span className='Grid-item-required'>*</span></Grid>
                            <Grid item md={9} className='Grid-item'>
                                <div className={(isFormError && !visaImage) ? "File-upload-div-error" : "File-upload-div"}>
                                    <FileUploader handleChange={file => (setVisaImage(file))} maxSize={1} onSizeError={() => fileUploadSizeError()}
                                        name="file" label="Browse or Drag and Drop Image. Please upload an image of the visa information page (Max Size: 1Mb)." types={["JPG", "PNG", "JPEG"]} />
                                </div>
                            </Grid>
                        </Grid>
                        <div className='Button-grid'>
                            <Button variant="contained" color="secondary" className="Submit-button" onClick={() => handleSubmit()}>Submit</Button>
                            <Button variant="outlined" color="primary" className="Reset-button" onClick={() => handleClose()}>Cancel</Button>
                        </div>
                    </div>

                </div>
            </Dialog >

        </div>

    );
}

export default AddVisaDialog;
